import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { Button } from '@client/components/atoms'
import { isApp } from '@shared/platform'

const Container = styled.div<{ $disabled: boolean }>`
  display: flex;
  flex: 1;

  .google-button {
    background-color: ${(props): string => props.$disabled ? props.theme.colors.whites.desertStorm : props.theme.colors.misc.googleRed};
    border-color: ${(props): string => props.$disabled ? props.theme.colors.whites.desertStorm : props.theme.colors.misc.googleRed};
  }

  .google-button:hover {
    opacity: 0.6;
  }
`

export interface GoogleSocialButtonProps {
  disabled: boolean
  action?: 'SIGN UP' | 'LOGIN'
  className?: string
  didAuthenticateWithGoogle: (token: string) => void
}

interface GoogleSocialButtonState {
  disabled: boolean
  loading: boolean
}

const DEFAULT_STATE: GoogleSocialButtonState = {
  disabled: false,
  loading: false,
}

export function GoogleSocialButton({ disabled, action = 'SIGN UP', className, didAuthenticateWithGoogle }: GoogleSocialButtonProps): JSX.Element {

  const { addToast } = useToasts()

  const [state, setState] = useState<GoogleSocialButtonState>({ ...DEFAULT_STATE })

  const buttonText = state.disabled ? `GOOGLE ${action} DISABLED` : `${action}  WITH GOOGLE`

  useEffect(() => {
    let mounted = true

    if (mounted) {

      if (isApp()) {
        // make sure button is enabled for capacitor app
        setState((prevState) => update(prevState, {
          disabled: { $set: false },
        }))
      } else {
        console.warn('Do not use Google Social button within a web app, use the GoogleIdentityServicesButton component instead.')

        setState((prevState) => update(prevState, {
          disabled: { $set: true },
        }))
      }
    }

    return function cleanup() {
      mounted = false
    }
  }, [])

  const signInWithCapacitorPlugin = async () => {
    try {
      const { SocialLogin } = await import('@capgo/capacitor-social-login')

      const res = await SocialLogin.login({
        provider: 'google',
        options: {},
      })

      if (res.result.responseType === 'online' && res.result.idToken) {
        didAuthenticateWithGoogle(res.result.idToken)
      } else {
        addToast('Unable to conduct Google sign-in.', {
          appearance: 'error',
          autoDismiss: true,
        })
      }

    } catch (error) {
      addToast('Unable to conduct Google sign-in.', {
        appearance: 'error',
        autoDismiss: true,
      })

      console.error('Error during Capacitor Google login:', JSON.stringify(error))
    } finally {
        _setLoading(false)
    }
  }

  const _handleClick = async (): Promise<void> => {
    if (isApp()) {
      _setLoading(true)
      signInWithCapacitorPlugin()
    }
  }

  const _setLoading = (loading: boolean): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: loading },
    }))
  }

  const actuallyDisabled = state.disabled || disabled

  return (
    <Container className={className} $disabled={actuallyDisabled}>
      <Button
        title={buttonText}
        disabled={actuallyDisabled || state.loading}
        loading={state.loading}
        onClick={_handleClick}
        className='google-button'
        fullWidth />
    </Container>
  )
}
